/**
 * DomainEventEnvelope — the ONE wire shape of the push plane (RFC §6b).
 *
 * Every input to a context store — SSE frame, poll result, call-plane read —
 * is expressed as versioned envelopes into the same `apply()`, so
 * reconciliation is free and double-apply is impossible by construction
 * (the idempotency law lives in the store; the shape lives here).
 *
 * `version` is per-stream and 1-based monotonically increasing (a store's
 * `lastApplied` starts at 0, so `fromVersion = lastApplied + 1` replays from
 * the first event). Frames are ArkType-validated before apply — never trust
 * the wire.
 */

import { type } from "arktype";

export interface DomainEventEnvelope {
  /** e.g. `Agg::career::{user}::{Type}[::{id}]` */
  streamId: string;
  /** Per-stream, 1-based, monotonically increasing. */
  version: number;
  /** e.g. `"searchRun.DiscoveryProgressed"` */
  type: string;
  /** ArkType-validated app-side before entity folds consume it. */
  payload: unknown;
}

const EnvelopeSchema = type({
  streamId: "string > 0",
  version: "number.integer >= 1",
  type: "string > 0",
  payload: "unknown",
});

/** Thrown by `parseEnvelope` on an invalid frame. Self-explaining, carries the raw frame. */
export class EnvelopeParseError extends Error {
  readonly raw: unknown;

  constructor(summary: string, raw: unknown) {
    super(
      `Invalid DomainEventEnvelope on the wire (dropping the frame is safe — ` +
        `resume() re-replays from lastApplied+1): ${summary}`
    );
    this.name = "EnvelopeParseError";
    this.raw = raw;
  }
}

/**
 * Validate a raw envelope object. Throws `EnvelopeParseError` naming every
 * violated constraint.
 */
export function parseEnvelope(raw: unknown): DomainEventEnvelope {
  const out = EnvelopeSchema(raw);
  if (out instanceof type.errors) {
    throw new EnvelopeParseError(out.summary, raw);
  }
  return out;
}

/**
 * FRAME CONTRACT (RFC §3a: "N events in ONE SSE frame produce one flush"):
 * an SSE `data:` line carries ONE envelope object OR an ARRAY of envelopes.
 * The server groups same-tick appends of a stream into ONE array frame and
 * sets the frame `id:` to the LAST envelope's version; singleton appends stay
 * single-envelope frames. Grouping MUST happen server-side: the browser
 * delivers each SSE message as its own MACROTASK, so client-side microtask
 * coalescing can never merge separate frames (empirically confirmed in
 * Chrome — 50 single-envelope frames = 50 renders).
 *
 * EVERY element is validated; ANY invalid element rejects the WHOLE frame
 * (throws — the channel drops it via onFrameError; the poll→resume path
 * heals the gap). An empty array is a valid, empty frame.
 */
export function parseFrame(raw: unknown): DomainEventEnvelope[] {
  if (Array.isArray(raw)) return raw.map((element) => parseEnvelope(element));
  return [parseEnvelope(raw)];
}
